import type { AppMenuItem } from '@workspace/ui';

export interface AppMenuConfig {
  sideMenu: AppMenuItem[];
  topMenu: AppMenuItem[];
  userMenu: AppMenuItem[];
}

const STATUS_INVENTORY_SCOPES = [
  'tenant.status.manage',
  'tenant.status.inventory.manage',
  'platform.manage'
] as const;

const NOTIFICATION_ADMIN_SCOPES = [
  'tenant.notifications.manage',
  'platform.manage'
] as const;

const TENANT_USERS_SCOPES = [
  'tenant.members.manage',
  'tenant.manage',
  'platform.manage'
] as const;

const TENANT_IDENTITY_SCOPES = [
  'tenant.identity-providers.view',
  'tenant.identity-providers.manage',
  'platform.manage'
] as const;

const OWNER_COMMERCE_SCOPES = [
  'tenant.catalog.manage',
  'tenant.pricebooks.manage',
  'tenant.commissions.manage',
  'tenant.commerce.manage',
] as const;

const RESELLER_COMMERCE_SCOPES = [
  'tenant.catalog.view',
  'tenant.orders.view',
  'tenant.carts.view',
  'tenant.commissions.view',
] as const;

export type MenuItem = AppMenuItem;

export function buildMenuConfig(scopes?: string[]): AppMenuConfig {
  const scopeSet = new Set(scopes || []);
  const includeOwnerCommerce = !scopes || OWNER_COMMERCE_SCOPES.some(scope => scopeSet.has(scope));
  const includeResellerCommerce = !!scopes && RESELLER_COMMERCE_SCOPES.some(scope => scopeSet.has(scope));
  const includeTenantIdentity = !scopes || TENANT_IDENTITY_SCOPES.some(scope => scopeSet.has(scope));

  const sideMenu: AppMenuItem[] = [
    {
      id: 'portal.home',
      label: 'Home',
      icon: 'house',
      children: [
        {
          id: 'portal.home.overview',
          label: 'Overview',
          route: '/dashboard',
          icon: 'house'
        },
        {
          id: 'portal.support',
          label: 'Support',
          route: '/support',
          icon: 'message-circle'
        }
      ]
    },
    {
      id: 'threat-protection',
      label: 'Threat Protection',
      icon: 'shield',
      requiredScopes: ['tenant.manage', 'platform.manage'],
      children: [
        { id: 'threat-protection.overview', label: 'Overview', route: '/threat-protection/overview', icon: 'shield', requiredScopes: ['tenant.manage', 'platform.manage'] },
        { id: 'threat-protection.ips', label: 'IP Reputation', route: '/threat-protection/ips', icon: 'shield', requiredScopes: ['tenant.manage', 'platform.manage'] },
        { id: 'threat-protection.bans', label: 'Temporary Bans', route: '/threat-protection/bans', icon: 'shield', requiredScopes: ['tenant.manage', 'platform.manage'] },
        { id: 'threat-protection.sources', label: 'Sources', route: '/threat-protection/sources', icon: 'shield', requiredScopes: ['tenant.manage', 'platform.manage'] },
        { id: 'threat-protection.allowlist', label: 'Allowlist', route: '/threat-protection/allowlist', icon: 'shield', requiredScopes: ['tenant.manage', 'platform.manage'] },
      ]
    },
    {
      id: 'api-gateway',
      label: 'API Gateway',
      icon: 'server',
      children: [
        {
          id: 'api-gateway.access',
          label: 'API Access',
          route: '/api-gateway/access',
          icon: 'key'
        },
        {
          id: 'api-gateway.console',
          label: 'Developer Console',
          route: '/api-gateway/console',
          icon: 'terminal'
        }
      ]
    },
    {
      id: 'commerce',
      label: 'Commerce',
      icon: 'shopping-bag',
      children: [
        {
          id: 'commerce.catalogs',
          label: 'Catalogs',
          route: '/commerce/catalogs',
          icon: 'book-open'
        },
        {
          id: 'commerce.markets',
          label: 'Markets',
          route: '/commerce/markets',
          icon: 'map'
        },
        {
          id: 'commerce.access-points',
          label: 'Access Points',
          route: '/commerce/access-points',
          icon: 'wifi'
        },
        {
          id: 'commerce.data-setup',
          label: 'Data Setup',
          route: '/commerce/data-setup',
          icon: 'database'
        },
        {
          id: 'commerce.pricebooks',
          label: 'Pricebooks',
          route: '/commerce/pricebooks',
          icon: 'dollar-sign'
        },
        {
          id: 'commerce.commission-plans',
          label: 'Commission Plans',
          route: '/commerce/commission-plans',
          icon: 'file-text'
        },
        {
          id: 'commerce.commission-ledger',
          label: 'Commission Ledger',
          route: '/commerce/commission-ledger',
          icon: 'receipt'
        },
        {
          id: 'commerce.storefronts',
          label: 'Storefronts',
          route: '/commerce/storefronts',
          icon: 'store'
        },
        {
          id: 'commerce.publications',
          label: 'Publications',
          route: '/commerce/publications',
          icon: 'layers'
        },
        {
          id: 'commerce.entitlements',
          label: 'Entitlements',
          route: '/commerce/entitlements',
          icon: 'shield-check'
        },
        {
          id: 'commerce.linked-resellers',
          label: 'Linked Resellers',
          route: '/commerce/linked-resellers',
          icon: 'network'
        },
        {
          id: 'commerce.assignments',
          label: 'Assignments',
          route: '/commerce/assignments',
          icon: 'user-check'
        },
        {
          id: 'commerce.discounts',
          label: 'Discounts',
          route: '/commerce/discounts',
          icon: 'percent'
        },
        {
          id: 'commerce.carts',
          label: 'Carts',
          route: '/commerce/carts',
          icon: 'shopping-cart'
        },
        {
          id: 'commerce.quotes',
          label: 'Quotes',
          route: '/commerce/quotes',
          icon: 'file-text'
        },
        {
          id: 'commerce.orders',
          label: 'Orders',
          route: '/commerce/orders',
          icon: 'package'
        }
      ]
    },
    {
      id: 'maps',
      label: 'Maps',
      icon: 'map',
      children: [
        {
          id: 'maps.sources',
          label: 'Sources',
          route: '/maps/sources',
          icon: 'database'
        },
        {
          id: 'maps.styles',
          label: 'Styles',
          route: '/maps/styles',
          icon: 'palette'
        },
        {
          id: 'maps.custom-maps',
          label: 'Custom Maps',
          route: '/maps/custom-maps',
          icon: 'compass'
        },
        {
          id: 'maps.sprites',
          label: 'Sprites',
          route: '/maps/sprites',
          icon: 'image'
        },
        {
          id: 'maps.glyphs',
          label: 'Glyphs',
          route: '/maps/glyphs',
          icon: 'type'
        }
      ]
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: 'credit-card',
      children: [
        {
          id: 'payments.providers',
          label: 'Payment Providers',
          route: '/payments/providers',
          icon: 'credit-card'
        }
      ]
    },
    {
      id: 'sales',
      label: 'Sales',
      icon: 'trending-up',
      children: [
        {
          id: 'sales.leads',
          label: 'Leads',
          route: '/sales/leads',
          icon: 'users',
          requiredScopes: ['tenant.quotes.view', 'tenant.quotes.manage', 'tenant.orders.view', 'tenant.orders.manage', 'platform.manage']
        },
        {
          id: 'sales.map',
          label: 'Sales Map',
          route: '/sales',
          icon: 'map'
        }
      ]
    },
    {
      id: 'status',
      label: 'Status',
      icon: 'activity',
      requiredScopes: [...STATUS_INVENTORY_SCOPES],
      children: [
        {
          id: 'status.public-map',
          label: 'Public Map',
          route: '/status/public-map',
          icon: 'globe',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.collection',
          label: 'Collection health',
          route: '/status/operations/collection',
          icon: 'activity',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.hubs',
          label: 'Hubs',
          route: '/status/inventory/hubs',
          icon: 'map-pin',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.devices',
          label: 'Devices',
          route: '/status/inventory/devices',
          icon: 'server',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.links',
          label: 'Links',
          route: '/status/inventory/links',
          icon: 'link',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.models',
          label: 'Device models',
          route: '/status/inventory/models',
          icon: 'cpu',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.auth',
          label: 'SNMP credentials',
          route: '/status/inventory/auth',
          icon: 'lock',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.overlays',
          label: 'Collection profiles',
          route: '/status/inventory/overlays',
          icon: 'layers',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        },
        {
          id: 'status.entities',
          label: 'Hardware',
          route: '/status/inventory/entities',
          icon: 'layers',
          requiredScopes: [...STATUS_INVENTORY_SCOPES]
        }
      ]
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'mail',
      requiredScopes: [...NOTIFICATION_ADMIN_SCOPES],
      children: [
        {
          id: 'notifications.channels',
          label: 'Channels',
          route: '/notifications/channels',
          icon: 'send',
          requiredScopes: [...NOTIFICATION_ADMIN_SCOPES]
        },
        {
          id: 'notifications.recipients',
          label: 'Recipients',
          route: '/notifications/recipients',
          icon: 'users',
          requiredScopes: [...NOTIFICATION_ADMIN_SCOPES]
        },
        {
          id: 'notifications.routes',
          label: 'Routes',
          route: '/notifications/routes',
          icon: 'git-merge',
          requiredScopes: [...NOTIFICATION_ADMIN_SCOPES]
        },
        {
          id: 'notifications.activity',
          label: 'Activity',
          route: '/notifications/activity',
          icon: 'activity',
          requiredScopes: [...NOTIFICATION_ADMIN_SCOPES]
        }
      ]
    },
    {
      id: 'users',
      label: 'Users',
      icon: 'user-check',
      requiredScopes: [...TENANT_USERS_SCOPES],
      children: [
        {
          id: 'users.active',
          label: 'Active Users',
          route: '/users/active',
          icon: 'users',
          requiredScopes: [...TENANT_USERS_SCOPES]
        },
        {
          id: 'users.invites',
          label: 'Invite Links',
          route: '/users/invites',
          icon: 'link',
          requiredScopes: [...TENANT_USERS_SCOPES]
        }
      ]
    },
    ...(includeTenantIdentity ? [{
      id: 'authentication',
      label: 'Authentication',
      icon: 'shield-check',
      requiredScopes: [...TENANT_IDENTITY_SCOPES],
      children: [
        {
          id: 'authentication.oidc-providers',
          label: 'OIDC Providers',
          route: '/settings/identity',
          icon: 'key',
          requiredScopes: [...TENANT_IDENTITY_SCOPES]
        }
      ]
    }] : []),
    {
      id: 'dashboard.settings',
      label: 'Settings',
      icon: 'settings',
      children: [
        {
          id: 'settings.appearance',
          label: 'Appearance',
          route: '/settings',
          icon: 'palette'
        }
      ]
    }
  ];

  const filteredSideMenu = sideMenu.filter(item => {
    if (item.id === 'commerce') {
      return includeOwnerCommerce;
    }
    return true;
  });

  if (includeResellerCommerce) {
    const salesIndex = filteredSideMenu.findIndex(item => item.id === 'sales');
    const insertIndex = salesIndex >= 0 ? salesIndex : filteredSideMenu.length;
    filteredSideMenu.splice(insertIndex, 0, {
      id: 'reseller',
      label: 'Agent',
      icon: 'briefcase',
      children: [
        {
          id: 'reseller.entitlements',
          label: 'Entitlements',
          route: '/reseller/entitlements',
          icon: 'shield-check'
        },
        {
          id: 'reseller.linked-sellers',
          label: 'Linked Sellers',
          route: '/reseller/linked-sellers',
          icon: 'link'
        },
        {
          id: 'reseller.quotes',
          label: 'Quotes',
          route: '/agent/quotes',
          icon: 'file-text'
        },
        {
          id: 'reseller.carts',
          label: 'Carts',
          route: '/reseller/carts',
          icon: 'shopping-cart'
        },
        {
          id: 'reseller.orders',
          label: 'Orders',
          route: '/reseller/orders',
          icon: 'package'
        },
        {
          id: 'reseller.commission-ledger',
          label: 'Commission Ledger',
          route: '/reseller/commission-ledger',
          icon: 'receipt'
        }
      ]
    });
  }

  return {
    sideMenu: filteredSideMenu,
    topMenu: [],
    userMenu: [
      {
        id: 'portal.help',
        label: 'Help',
        route: '/help',
        icon: 'book-open'
      },
      {
        id: 'dashboard.settings',
        label: 'Settings',
        route: '/settings',
        icon: 'settings'
      }
    ]
  };
}

export const menuConfig = buildMenuConfig();
